import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FileBox,
  FilePlus,
  Plug,
  Plus,
  Search,
  Settings,
  Sparkles,
  Trash2,
  Zap,
} from "lucide-react";
import {
  deleteGenerativeTriggers,
  getGenerativeTriggers,
  updateGenerativeTrigger,
} from "../../../../services/generative-triggers";
import {
  createTriggerAction,
  deleteTriggerAction,
  getTriggerActions,
  updateTriggerAction,
} from "../../../../services/generative-actions";
import {
  getMCPServers,
  getMCPServerTools,
  getAssistantMCPServers,
  createAssistantMCPServer,
  deleteAssistantMCPServer,
  updateAssistantMCPServerTools,
} from "../../../../services/mcp-server";
import TriggerCreate from "./Create";
import TriggerEdit from "./Edit";
import ActionEdit from "./Actions/Edit";
import AddTemplateModal from "./Add-Templates";
import { Button } from "../../../../components/ui/button";
import { Card } from "../../../../components/ui/card";
import { Avatar } from "../../../../components/ui/avatar";
import { Badge } from "../../../../components/ui/badge";
import { Switch } from "../../../../components/ui/switch";
import { ConfirmDialog, Dialog, DialogHeader, DialogContent, DialogFooter } from "../../../../components/ui/dialog";
import { EmptyState } from "../../../../components/ui/empty-state";
import { Pagination } from "../../../../components/ui/pagination";
import { Tooltip } from "../../../../components/ui/tooltip";
import { DropdownMenu } from "../../../../components/ui/dropdown-menu";
import { Spinner } from "../../../../components/ui/spinner";
import { Select } from "../../../../components/ui/select";
import { toast } from 'sonner'

export default function Trigger({ assistantId }) {
  const [loading, setLoading] = useState(true);
  const [triggers, setTriggers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [total, setTotal] = useState(0);

  const refresh = useCallback(() => {
    setLoading(true);
    getGenerativeTriggers({ assistantId, page, perPage })
      .then(({ data, total }) => {
        setTriggers(data);
        setTotal(total);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Falha ao carregar gatilhos.");
      })
      .finally(() => setLoading(false));
  }, [assistantId, page, perPage]);

  useEffect(() => {
    if (!assistantId) return;
    refresh();
  }, [refresh, assistantId]);

  return (
    <>
      <TriggerCreate
        onSuccess={refresh}
        open={createOpen}
        assistantId={assistantId}
        setOpen={setCreateOpen}
      />
      <AddTemplateModal
        refresh={refresh}
        open={templateOpen}
        setOpen={setTemplateOpen}
        assistantId={assistantId}
      />

      <div className="flex flex-col gap-6">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <Zap size={18} style={{ color: "var(--dash-primary)" }} />
              <h2
                className="text-xl font-semibold tracking-tight"
                style={{ color: "var(--dash-text)" }}
              >
                Gatilhos
              </h2>
            </div>
            <p className="text-xs" style={{ color: "var(--dash-text-muted)" }}>
              Eventos que disparam ações automatizadas no seu assistente.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              leftIcon={FilePlus}
              onClick={() => setTemplateOpen(true)}
            >
              Adicionar template
            </Button>
            <Button
              variant="primary"
              leftIcon={Plus}
              onClick={() => setCreateOpen(true)}
            >
              Criar gatilho
            </Button>
          </div>
        </header>

        <MCPBindings assistantId={assistantId} />

        {loading ? (
          <Card className="flex items-center justify-center py-16">
            <Spinner size={20} />
          </Card>
        ) : triggers.length === 0 ? (
          <Card>
            <EmptyState
              icon={Sparkles}
              title="Nenhum gatilho configurado"
              description="Gatilhos são eventos que disparam ações no seu assistente. Use-os para criar integrações ou consultas durante o uso."
              action={
                <>
                  <Button
                    variant="primary"
                    leftIcon={Plus}
                    onClick={() => setCreateOpen(true)}
                  >
                    Criar primeiro gatilho
                  </Button>
                  <Link target="_blank" to="https://verboo.ai/docs">
                    <Button variant="outline">Documentação</Button>
                  </Link>
                </>
              }
            />
          </Card>
        ) : (
          <div className="flex flex-col gap-2">
            {triggers.map((item) => (
              <TriggerItem key={item.id} item={item} onSuccess={refresh} />
            ))}
            <Pagination
              page={page}
              pageSize={perPage}
              total={total}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>
    </>
  );
}

function TriggerItem({ item, onSuccess }) {
  const [editOpen, setEditOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [switchLoading, setSwitchLoading] = useState(false);
  const [actions, setActions] = useState(item.generativeActions);
  const [selectedAction, setSelectedAction] = useState(null);
  const [isActive, setIsActive] = useState(item.isActive);

  const isTemplate = useMemo(
    () => item.generativeTriggerTemplateID,
    [item]
  );
  const onClickTrigger = isTemplate ? null : () => setEditOpen(true);
  const isLimitReached = actions.length > 3;

  const getActions = useCallback(() => {
    getTriggerActions(item.assistantID, item.id)
      .then((res) => {
        setActions(() => res.data.reverse());
      })
      .catch((err) => {
        console.error(err);
        toast.error("Falha ao carregar ações.");
      });
  }, [item.assistantID, item.id]);

  const addAction = useCallback(
    async (data) => {
      return createTriggerAction(item.assistantID, item.id, data)
        .then(() => {
          toast.success("Ação criada com sucesso");
          onSuccess();
          getActions();
        })
        .catch((err) => {
          console.error(err);
          toast.error("Falha ao adicionar ação");
        });
    },
    [item.assistantID, item.id, onSuccess, getActions]
  );

  const updateAction = useCallback(
    async (action) => {
      await updateTriggerAction(
        item.assistantID,
        action.generativeTriggerID,
        action.id,
        action
      )
        .then(() => {
          toast.success("Ação atualizada com sucesso");
          onSuccess();
          getActions();
        })
        .catch((err) => {
          console.error(err);
          toast.error("Falha ao atualizar ação");
        });
    },
    [item.assistantID, onSuccess, getActions]
  );

  const deleteAction = useCallback(
    async (actionID) => {
      return deleteTriggerAction(item.assistantID, item.id, actionID)
        .then(() => {
          onSuccess();
          getActions();
        })
        .catch((err) => {
          console.error(err);
          toast.error("Falha ao deletar ação.");
        });
    },
    [item.assistantID, item.id, onSuccess, getActions]
  );

  const onDelete = useCallback(() => {
    setDeleting(true);
    deleteGenerativeTriggers(item.assistantID, item.id)
      .then(() => {
        onSuccess();
        toast.success("Gatilho deletado com sucesso.");
        setConfirmOpen(false);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Falha ao deletar gatilho.");
      })
      .finally(() => setDeleting(false));
  }, [item.assistantID, item.id, onSuccess]);

  const onSwitch = useCallback(
    async (checked) => {
      setSwitchLoading(true);
      const updatedItem = { ...item, isActive: checked };
      delete updatedItem.args;
      await updateGenerativeTrigger(item.id, item.assistantID, updatedItem)
        .then(({ data }) => {
          setIsActive(data.isActive);
          toast.success("Estado do gatilho atualizado!");
        })
        .catch((err) => {
          toast.error("Erro ao atualizar o gatilho");
          console.error(err);
        })
        .finally(() => setSwitchLoading(false));
    },
    [item]
  );

  const dropdownItems = useMemo(
    () =>
      actions.slice(0, 4).map((_, index) => ({
        key: index,
        label: `Ação ${index + 1}`,
        onClick: () => setSelectedAction(actions[index]),
      })),
    [actions]
  );

  return (
    <>
      <TriggerEdit
        onSuccess={onSuccess}
        open={editOpen}
        initValues={item}
        assistantId={item.assistantID}
        setOpen={setEditOpen}
      />
      <ActionEdit
        trigger={item}
        data={selectedAction}
        clear={() => setSelectedAction(null)}
        addAction={addAction}
        deleteAction={deleteAction}
        updateAction={updateAction}
      />

      <Card className="flex items-center gap-4 p-4 transition-all hover:border-[var(--dash-primary-border)]">
        <Avatar
          variant={isTemplate ? "default" : "primary"}
          icon={isTemplate ? FileBox : Zap}
          size="md"
        />
        <Tooltip
          content={isTemplate ? "Templates não podem ser editados" : null}
          side="top"
        >
          <div
            className="flex min-w-0 flex-1 cursor-pointer flex-col gap-1"
            onClick={onClickTrigger ?? undefined}
            role="button"
            tabIndex={0}
            style={{ cursor: isTemplate ? "default" : "pointer" }}
          >
            <div className="flex items-center gap-2">
              <h3
                className="truncate text-sm font-semibold"
                style={{ color: "var(--dash-text)" }}
              >
                {item.args.name}
              </h3>
              {item.copilotOnly && <Badge variant="success">Copiloto</Badge>}
              {isTemplate && <Badge variant="muted">Template</Badge>}
            </div>
            <p
              className="truncate text-xs"
              style={{ color: "var(--dash-text-muted)" }}
            >
              {item.args.description}
            </p>
          </div>
        </Tooltip>

        <div className="flex shrink-0 items-center gap-2">
          {isTemplate ? (
            <Switch
              checked={isActive}
              onChange={onSwitch}
              disabled={switchLoading}
            />
          ) : (
            <DropdownMenu
              trigger={
                <Tooltip
                  content={isLimitReached ? "Limite de ações atingido" : null}
                >
                  <Button
                    variant="outline"
                    size="sm"
                    leftIcon={Plus}
                    disabled={isLimitReached}
                  >
                    Ações ({actions.length})
                  </Button>
                </Tooltip>
              }
              items={[
                ...dropdownItems,
                ...(actions.length > 0 ? [{ separator: true }] : []),
                {
                  key: "new",
                  label: "Nova ação",
                  icon: Plus,
                  onClick: () => setSelectedAction(-1),
                  disabled: isLimitReached,
                },
              ]}
            />
          )}
          <Tooltip content="Deletar gatilho">
            <Button
              variant="danger"
              size="icon-sm"
              onClick={() => setConfirmOpen(true)}
              disabled={deleting}
            >
              <Trash2 size={14} />
            </Button>
          </Tooltip>
        </div>
      </Card>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={onDelete}
        title="Deletar gatilho?"
        description="Esta ação irá remover o gatilho e todas as suas ações. Não é possível desfazer."
        confirmLabel="Deletar"
        loading={deleting}
      />
    </>
  );
}

/* ════════════════════════════════════════════════════════════════════
   MCP BINDINGS — servidores MCP conectados ao assistant
   ════════════════════════════════════════════════════════════════════ */

function MCPBindings({ assistantId }) {
  const [servers, setServers] = useState([]);
  const [bindings, setBindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectOpen, setConnectOpen] = useState(false);
  const [toolsDialog, setToolsDialog] = useState(null);
  const [disconnectTarget, setDisconnectTarget] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([getMCPServers(), getAssistantMCPServers(assistantId)])
      .then(([s, b]) => {
        setServers(s.data || []);
        setBindings(b.data || []);
      })
      .catch(() => toast.error("Erro ao carregar MCP servers."))
      .finally(() => setLoading(false));
  }, [assistantId]);

  useEffect(() => { load(); }, [load]);

  const boundIds = new Set(bindings.map((b) => b.mcpServerID));
  const unbound = servers.filter((s) => !boundIds.has(s.id));

  const handleConnect = useCallback((serverId) => {
    createAssistantMCPServer(assistantId, { mcpServerID: serverId, isEnabled: true })
      .then(() => { toast.success("MCP server conectado!"); setConnectOpen(false); load(); })
      .catch(() => toast.error("Erro ao conectar."));
  }, [assistantId, load]);

  const handleDisconnect = useCallback((bindId) => {
    deleteAssistantMCPServer(assistantId, bindId)
      .then(() => { toast.success("Desconectado."); setDisconnectTarget(null); load(); })
      .catch(() => toast.error("Erro ao desconectar."));
  }, [assistantId, load]);

  if (loading && bindings.length === 0 && servers.length === 0) return null;
  if (servers.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Plug size={15} style={{ color: "var(--dash-primary)" }} />
          <h3 className="text-sm font-semibold" style={{ color: "var(--dash-text)" }}>MCP Servers</h3>
          {bindings.length > 0 && (
            <Badge variant="primary">{bindings.length} conectado{bindings.length !== 1 ? "s" : ""}</Badge>
          )}
        </div>
        {unbound.length > 0 && (
          <Button variant="outline" size="sm" leftIcon={Plus} onClick={() => setConnectOpen(true)}>
            Conectar MCP
          </Button>
        )}
      </div>

      {bindings.length > 0 && (
        <div className="flex flex-col gap-2">
          {bindings.map((binding) => {
            const server = binding.mcpServer || servers.find((s) => s.id === binding.mcpServerID);
            if (!server) return null;
            const enabledTools = binding.tools?.filter((t) => t.isEnabled) || [];
            return (
              <Card key={binding.id} className="flex items-center gap-4 p-4">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" style={{ background: "var(--dash-primary-soft)", border: "1px solid var(--dash-primary-border)", color: "var(--dash-primary)" }}>
                  <Plug size={16} />
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate text-sm font-medium" style={{ color: "var(--dash-text)" }}>{server.name}</span>
                  <span className="truncate text-[11px]" style={{ color: "var(--dash-text-muted)" }}>{server.url}</span>
                </div>
                <Badge variant={enabledTools.length > 0 ? "primary" : "muted"} dot>
                  {enabledTools.length} tool{enabledTools.length !== 1 ? "s" : ""}
                </Badge>
                <div className="flex items-center gap-1">
                  <Tooltip content="Configurar tools">
                    <Button variant="outline" size="icon-sm" onClick={() => setToolsDialog({ server, binding })}>
                      <Settings size={14} />
                    </Button>
                  </Tooltip>
                  <Tooltip content="Desconectar">
                    <Button variant="danger" size="icon-sm" onClick={() => setDisconnectTarget(binding.id)}>
                      <Trash2 size={14} />
                    </Button>
                  </Tooltip>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <ConnectMCPDialog
        open={connectOpen}
        servers={unbound}
        onClose={() => setConnectOpen(false)}
        onConnect={handleConnect}
      />

      {toolsDialog && (
        <MCPToolsDialog
          open
          server={toolsDialog.server}
          binding={toolsDialog.binding}
          assistantId={assistantId}
          onClose={() => setToolsDialog(null)}
          onSaved={() => { setToolsDialog(null); load(); }}
        />
      )}

      <ConfirmDialog
        open={!!disconnectTarget}
        onClose={() => setDisconnectTarget(null)}
        onConfirm={() => handleDisconnect(disconnectTarget)}
        title="Desconectar MCP server?"
        description="As tools deste servidor não estarão mais disponíveis para o assistente."
        confirmLabel="Desconectar"
      />
    </div>
  );
}

function ConnectMCPDialog({ open, servers, onClose, onConnect }) {
  const [selected, setSelected] = useState(null);

  useEffect(() => { if (!open) setSelected(null); }, [open]);

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogHeader icon={Plug} title="Conectar MCP Server" description="Selecione um servidor da sua empresa para conectar ao assistente." />
      <DialogContent>
        {servers.length === 0 ? (
          <p className="py-4 text-center text-xs" style={{ color: "var(--dash-text-muted)" }}>
            Todos os servidores já estão conectados. Crie novos em Templates {'>'} MCP Servers.
          </p>
        ) : (
          <div className="flex flex-col gap-1">
            {servers.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSelected(s.id)}
                className="flex items-center gap-3 rounded-xl px-4 py-3 text-left transition-colors"
                style={{
                  background: selected === s.id ? "var(--dash-primary-soft)" : "var(--dash-surface-subtle)",
                  border: selected === s.id ? "1px solid var(--dash-primary-border)" : "1px solid transparent",
                }}
              >
                <Plug size={14} style={{ color: selected === s.id ? "var(--dash-primary)" : "var(--dash-text-muted)" }} />
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-medium" style={{ color: "var(--dash-text)" }}>{s.name}</span>
                  <span className="text-[11px]" style={{ color: "var(--dash-text-muted)" }}>{s.url}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button variant="primary" disabled={!selected} onClick={() => onConnect(selected)}>Conectar</Button>
      </DialogFooter>
    </Dialog>
  );
}

function MCPToolsDialog({ open, server, binding, assistantId, onClose, onSaved }) {
  const [tools, setTools] = useState([]);
  const [enabledMap, setEnabledMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !server) return;
    setLoading(true);
    getMCPServerTools(server.id)
      .then(({ data }) => {
        setTools(data || []);
        const map = {};
        for (const t of data || []) {
          const existing = binding?.tools?.find((bt) => bt.toolName === t.name);
          map[t.name] = existing ? existing.isEnabled : false;
        }
        setEnabledMap(map);
      })
      .catch(() => toast.error("Erro ao listar tools."))
      .finally(() => setLoading(false));
  }, [open, server, binding]);

  const toggle = (name) => setEnabledMap((m) => ({ ...m, [name]: !m[name] }));
  const allEnabled = tools.length > 0 && tools.every((t) => enabledMap[t.name]);
  const toggleAll = () => {
    const newVal = !allEnabled;
    setEnabledMap(Object.fromEntries(tools.map((t) => [t.name, newVal])));
  };

  const handleSave = () => {
    const payload = Object.entries(enabledMap).map(([toolName, isEnabled]) => ({ toolName, isEnabled }));
    setSaving(true);
    updateAssistantMCPServerTools(assistantId, binding.id, payload)
      .then(() => { toast.success("Tools atualizadas!"); onSaved(); })
      .catch(() => toast.error("Erro ao salvar tools."))
      .finally(() => setSaving(false));
  };

  const enabledCount = Object.values(enabledMap).filter(Boolean).length;

  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <DialogHeader icon={Settings} title={`Tools — ${server?.name}`} description={`${tools.length} disponíveis, ${enabledCount} ativas`} />
      <DialogContent>
        {loading ? (
          <div className="flex items-center justify-center py-8"><Spinner size={20} /></div>
        ) : tools.length === 0 ? (
          <p className="py-6 text-center text-xs" style={{ color: "var(--dash-text-muted)" }}>Nenhuma tool encontrada neste servidor.</p>
        ) : (
          <>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[11px]" style={{ color: "var(--dash-text-muted)" }}>{enabledCount} de {tools.length} ativas</span>
            <Button variant="outline" size="sm" type="button" onClick={toggleAll}>
              {allEnabled ? "Desativar todas" : "Ativar todas"}
            </Button>
          </div>
          <div className="flex flex-col gap-1 max-h-[400px] overflow-y-auto pr-1">
            {tools.map((tool) => (
              <div
                key={tool.name}
                className="flex items-center justify-between rounded-xl px-4 py-3 transition-colors"
                style={{
                  background: enabledMap[tool.name] ? "var(--dash-primary-soft)" : "var(--dash-surface-subtle)",
                  border: enabledMap[tool.name] ? "1px solid var(--dash-primary-border)" : "1px solid transparent",
                }}
              >
                <div className="flex flex-col gap-0.5 pr-4">
                  <span className="text-xs font-medium" style={{ color: "var(--dash-text)" }}>{tool.name}</span>
                  {tool.description && <span className="text-[11px] leading-snug" style={{ color: "var(--dash-text-muted)" }}>{tool.description}</span>}
                </div>
                <Switch size="sm" checked={!!enabledMap[tool.name]} onChange={() => toggle(tool.name)} />
              </div>
            ))}
          </div>
          </>
        )}
      </DialogContent>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button variant="primary" onClick={handleSave} loading={saving}>Salvar ({enabledCount} ativas)</Button>
      </DialogFooter>
    </Dialog>
  );
}
